import React, { useEffect, useState } from "react";
import { Drawer, Typography, Avatar } from "antd";
import {
  CalendarOutlined,
  UserOutlined,
  ExpandOutlined,
  EditOutlined,
  TagOutlined,
  NumberOutlined,
  CheckSquareOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { SimpleTagManager } from "./SimpleTagManager";
import { useTags } from "hooks";
import DOMPurify from "dompurify";

const { Title, Text } = Typography;

const ProjectDetailsDrawer = ({
  visible,
  project,
  onClose,
  onEditProject,
  onTagsUpdate,
}) => {
  const { tags: allTags, loading } = useTags();
  const [isExpanded, setIsExpanded] = useState(false);
  const [localTags, setLocalTags] = useState(project?.tags || []);

  useEffect(() => {
    setLocalTags(project?.tags || []);
  }, [project]);

  if (!project) return null;

  const metaItems = [
    {
      icon: <NumberOutlined />,
      label: "Project Code",
      value: <Text>{project.code}</Text>,
    },
    {
      icon: <UserOutlined />,
      label: "Owner",
      value: (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Avatar size={22}>
            {project.ownerName?.slice(0, 2).toUpperCase()}
          </Avatar>
          <Text>{project.ownerName}</Text>
        </div>
      ),
    },
    {
      icon: <CheckSquareOutlined />,
      label: "Target Points",
      value: <Text>{project.targetPoints || "Null"}</Text>,
    },
    {
      icon: <FolderOpenOutlined />,
      label: "Iterations",
      value: <Text>{project.iterationCount}</Text>,
    },
    {
      icon: <CheckSquareOutlined />,
      label: "Tasks",
      value: <Text>{project.taskCount}</Text>,
    },
    // {
    //   icon: <TagOutlined />,
    //   label: "Tags",
    //   value: (
    //     <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
    //       {project.tags?.map((tag) => (
    //         <SoftTag key={tag.id} color="#eef2ff" textColor="#4338ca">
    //           {tag.name}
    //         </SoftTag>
    //       ))}
    //     </div>
    //   ),
    // },
    {
      icon: <CalendarOutlined />,
      label: "Created At",
      value: (
        <Text>
          {project.createdAt
            ? dayjs(project.createdAt).format("MMM D, YYYY HH:mm")
            : "—"}
        </Text>
      ),
    },
    {
      icon: <CalendarOutlined />,
      label: "Updated At",
      value: (
        <Text>
          {project.updatedAt
            ? dayjs(project.updatedAt).format("MMM D, YYYY HH:mm")
            : "—"}
        </Text>
      ),
    },
  ];

  return (
    <Drawer
      open={visible}
      onClose={onClose}
      placement="right"
      size={isExpanded ? "100%" : 520}
      zIndex={99999}
      styles={{ padding: 24 }}
      title={null}
      closable={false}
    >
      <div
        style={{
          maxWidth: isExpanded ? 700 : "100%",
          margin: isExpanded ? "0 auto" : 0,
          fontFamily: "Inter, sans-serif",
        }}
      >
        {/* Breadcrumb / context */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            <span style={{ cursor: "pointer" }}>Projects</span>
            <span style={{ margin: "0 6px" }}>/</span>
            <span style={{ color: "#111827", fontWeight: 500 }}>
              {project.name}
            </span>
          </div>

          <div style={{ display: "flex", gap: 16 }}>
            <ExpandOutlined
              onClick={() => setIsExpanded((prev) => !prev)}
              style={{ cursor: "pointer" }}
            />
            <EditOutlined
              onClick={() => onEditProject(project)}
              style={{ cursor: "pointer" }}
            />
          </div>
        </div>

        {/* title */}
        <Title level={4} style={{ marginBottom: 16 }}>
          {project.name}
        </Title>

        {/* meta Section */}
        <div style={{ marginBottom: 20 }}>
          {metaItems.map((item, index) => (
            <MetaRow
              key={index}
              icon={item.icon}
              label={item.label}
              value={item.value}
            />
          ))}
        </div>

        <MetaRow
          icon={<TagOutlined />}
          label="Tags"
          value={
            <SimpleTagManager
              entityType="project"
              entityId={project.id}
              currentTags={localTags}
              availableTags={allTags}
              onChange={(updatedTags) => {
                setLocalTags(updatedTags);
                onTagsUpdate?.(project.id, updatedTags);
              }}
            />
          }
        />

        {/* description */}
        <div style={{ marginBottom: 24 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "#6b7280",
              fontSize: 13,
              marginBottom: 8,
            }}
          >
            <FolderOpenOutlined />
            <span>Description</span>
          </div>

          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 4,
              padding: 12,
              background: "#fafafa",
            }}
          >
            <div
              style={{ color: "#374151", fontSize: 13, margin: 0, padding: 0 }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  project.description || "<p>No description provided</p>",
                ),
              }}
            />
          </div>
        </div>
      </div>
    </Drawer>
  );
};

/* reusable Components */
const MetaRow = ({ icon, label, value }) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-start",
      marginBottom: 14,
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        color: "#6b7280",
        fontSize: 13,
        flex: 1,
      }}
    >
      {icon}
      <span>{label}</span>
    </div>

    <div style={{ flex: 2 }}>{value}</div>
  </div>
);

const SoftTag = ({ color, textColor, children }) => (
  <span
    style={{
      background: color,
      color: textColor,
      fontSize: 12,
      fontWeight: 500,
      padding: "2px 8px",
      borderRadius: 6,
      lineHeight: "20px",
      transition: "all 0.2s ease",
    }}
  >
    {children}
  </span>
);

export default ProjectDetailsDrawer;
